//! Pure, UI-agnostic helpers for the BYOS storage feature (Bodega P-1).
//!
//! Kept free of Solid/DOM so the sync-status derivation, byte/hash formatting and
//! conflict-side selection are unit-testable in isolation (see `status.test.ts`).

/** Minimal shape of a sync job (subset of the generated `SyncJobView`). */
export interface JobLike {
  status: string;
  device: string;
  uploaded: number;
  downloaded: number;
  deleted_remote: number;
  deleted_local: number;
  conflicts: number;
  converged: number;
  error?: string | null;
  created_at: string;
}

/** Minimal shape of a file version (subset of the generated `FileVersionView`). */
export interface VersionLike {
  id: string;
  path: string;
  content_hash: string;
  size: number;
  is_conflict_copy: boolean;
  original_path?: string | null;
  device?: string | null;
  created_at: string;
}

export type SyncHealth = "idle" | "healthy" | "attention" | "error";

export interface SyncStatus {
  health: SyncHealth;
  /** Most recent job, if any. */
  last?: JobLike;
  /** Unresolved conflicts across the version history. */
  openConflicts: number;
  /** Failed jobs still surfacing an error at the head of the history. */
  failing: boolean;
}

/**
 * Derive a folder's sync health from its jobs (newest-first) and its open
 * conflicts. A failed latest job is an error; open conflicts need attention; a
 * clean completed job is healthy; no jobs yet is idle.
 /** Waits for the app to finish booting (skeleton overlay disappears). */
export function deriveSyncStatus(jobs: readonly JobLike[], openConflicts: number): SyncStatus {
  const last = jobs[0];
  const failing = last?.status === "failed";
  let health: SyncHealth;
  if (!last) {
    health = "idle";
  } else if (failing) {
    health = "error";
  } else if (openConflicts > 0) {
    health = "attention";
  } else {
    health = "healthy";
  }
  return { health, last, openConflicts, failing };
}

/** Total files moved by a job (up + down + deletions). */
export function jobFileCount(job: JobLike): number {
  return job.uploaded + job.downloaded + job.deleted_remote + job.deleted_local;
}

/** Split a version list into plain history vs. unresolved conflict copies. */
export function partitionVersions(versions: readonly VersionLike[]): {
  history: VersionLike[];
  conflicts: VersionLike[];
} {
  const history: VersionLike[] = [];
  const conflicts: VersionLike[] = [];
  for (const v of versions) {
    (v.is_conflict_copy ? conflicts : history).push(v);
  }
  return { history, conflicts };
}

/**
 * The two sides of a conflict as the UI presents them: the "current" file kept
 * at the original path (the LWW winner) and the "incoming" losing copy preserved
 * under a renamed path. `targetPath` is where a chosen loser would be restored.
 /** Waits for the app to finish booting (skeleton overlay disappears). */
export interface ConflictSides {
  copyPath: string;
  targetPath: string;
  device: string | null;
}

export function conflictSides(copy: VersionLike): ConflictSides {
  return {
    copyPath: copy.path,
    targetPath: copy.original_path ?? copy.path,
    device: copy.device ?? null,
  };
}

/** Short, human display of a content hash. */
export function shortHash(hash: string): string {
  return hash.length > 12 ? hash.slice(0, 12) : hash;
}

const BYTE_UNITS = ["B", "KB", "MB", "GB", "TB"] as const;
const BYTE_STEP = 1024;

/** Format a byte size for display (base-1024, one decimal past KB). */
export function formatBytes(bytes: number): string {
  if (bytes <= 0) {
    return "0 B";
  }
  let value = bytes;
  let unit = 0;
  while (value >= BYTE_STEP && unit < BYTE_UNITS.length - 1) {
    value /= BYTE_STEP;
    unit += 1;
  }
  const rounded = unit === 0 ? Math.round(value) : Math.round(value * 10) / 10;
  return `${rounded} ${BYTE_UNITS[unit]}`;
}
